<template>
  <van-popup
    v-model:show="visible"
    :closeable="closeable"
    :close-on-click-overlay="closeOnClickOverlay"
    round
    safe-area-inset-bottom
    position="bottom"
  >
    <div class="flex flex-col">
      <!-- 标题区域 -->
      <div
        v-if="title"
        class="flex items-center gap-2 justify-center h-12 border-b border-border-light"
      >
        <van-icon v-if="icon" :name="icon" :color="iconColor" size="24" class="shrink-0" />
        <h3 class="text-base font-semibold text-text-primary m-0 leading-snug">
          {{ title }}
        </h3>
      </div>

      <!-- 内容区域 -->
      <div class="flex-1 py-2 px-3 min-h-[120px]">
        <!-- 提示信息 -->
        <section v-if="tipText" class="flex gap-2">
          <InfoIcon class="shrink-0 size-12 text-primary pt-2" />
          <div class="text-text-primary pt-2 pl-1">
            <h3 class="text-base font-bold">{{ tipText }}</h3>
            <p v-if="description" class="text-sm pt-2" v-html="description"></p>
          </div>
        </section>

        <!-- 描述文字 -->
        <p
          v-else-if="description"
          v-html="description"
          class="text-[15px] text-[#646566] leading-relaxed m-0 whitespace-pre-line"
        ></p>

        <!-- 插槽内容 -->
        <slot></slot>
      </div>

      <!-- 底部 -->
      <div class="space-y-2 py-2 px-4 border-t border-border-light">
        <div v-if="footerTip" class="text-center text-xs text-auxiliary-red leading-relaxed">
          {{ footerTip }}
        </div>
        <!-- 底部按钮区域 -->
        <div
          class="flex gap-3"
          :class="footerLayout === 'vertical' ? 'flex-col-reverse' : 'flex-row'"
        >
          <van-button
            v-if="showCancel"
            plain
            :loading="cancelLoading"
            class="flex-1 h-11 text-base rounded-lg !border-[#dcdee0] !text-[#646566] active:!bg-[#f7f8fa]"
            @click="handleCancel"
          >
            {{ cancelText }}
          </van-button>
          <van-button
            v-if="showConfirm"
            type="primary"
            :loading="confirmLoading"
            :disabled="confirmDisabled"
            class="flex-1 h-11 text-base"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </van-button>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import InfoIcon from '@/svg/tip/info.svg'

interface Props {
  show?: boolean
  title?: string
  description?: string
  icon?: string
  iconColor?: string
  tipText?: string
  tipType?: 'success' | 'warning' | 'error'
  showCancel?: boolean
  showConfirm?: boolean
  cancelText?: string
  confirmText?: string
  closeable?: boolean
  closeOnClickOverlay?: boolean
  footerTip?: string
  footerLayout?: 'horizontal' | 'vertical'
  cancelLoading?: boolean
  confirmLoading?: boolean
  confirmDisabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  showCancel: true,
  showConfirm: true,
  cancelText: '取消',
  confirmText: '确定',
  closeable: true,
  closeOnClickOverlay: true,
  footerLayout: 'horizontal',
  iconColor: '#3b7cff',
  tipType: 'success',
  cancelLoading: false,
  confirmLoading: false,
  confirmDisabled: false,
})

const emit = defineEmits<{
  'update:show': [value: boolean]
  cancel: []
  confirm: []
}>()

const visible = ref(props.show)

watch(
  () => props.show,
  (val) => {
    visible.value = val
  },
)

watch(visible, (val) => {
  emit('update:show', val)
})

const handleCancel = () => {
  emit('cancel')
  if (!props.cancelLoading) {
    visible.value = false
  }
}

const handleConfirm = () => {
  emit('confirm')
  if (!props.confirmLoading) {
    visible.value = false
  }
}
</script>

<style scoped>
/* 渐变背景需要保留在 style 中，因为 Tailwind 的任意值不支持 linear-gradient 类名 */
</style>
